/**
 * CopilotAssistBar — a slim, inline row of AI rewrite chips.
 *
 * Meant for compact chat / reply inputs where a full CopilotTextAssist wouldn't
 * fit. Given the current draft text, calls /api/copilot/assist and swaps in
 * the rewritten version. Includes an Undo pill after a rewrite lands.
 */
import { useState } from "react";
import { Wand2, Loader2, Undo2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const MODES = [
  { key: "shorten",  label: "Shorter" },
  { key: "friendly", label: "Friendlier" },
  { key: "formal",   label: "Formal" },
  { key: "grammar",  label: "Grammar" },
];

export default function CopilotAssistBar({ text, onReplace, contextHint = "tenant/landlord message" }) {
  const [loading, setLoading] = useState(null);
  const [prev, setPrev] = useState(null);
  const [warn, setWarn] = useState("");

  async function run(mode) {
    setLoading(mode);
    setWarn("");
    try {
      const r = await fetch(`${API}/api/copilot/assist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, mode, context: contextHint }),
      });
      const data = await r.json();
      if (data?.text && data.text !== text) {
        setPrev(text);
        onReplace(data.text);
        if (data.source === "fallback") {
          setWarn("Backup mode — using a proven template right now.");
          setTimeout(() => setWarn(""), 4500);
        }
      }
    } catch {
      setWarn("Assist failed");
      setTimeout(() => setWarn(""), 2500);
    } finally {
      setLoading(null);
    }
  }

  function undo() {
    if (prev !== null) { onReplace(prev); setPrev(null); }
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5 px-1" data-testid="copilot-assist-bar">
      <div className="flex items-center gap-1 text-[11px] text-slate-500 mr-1">
        <Wand2 className="w-3 h-3 text-emerald-600" />
        <span className="font-medium">Copilot</span>
      </div>
      {MODES.map((m) => (
        <button
          key={m.key}
          type="button"
          onClick={() => run(m.key)}
          disabled={loading !== null}
          data-testid={`copilot-bar-${m.key}`}
          className="text-[11px] px-2 py-0.5 rounded-full border border-slate-200 bg-white hover:border-emerald-400 hover:bg-emerald-50 text-slate-700 disabled:opacity-40 flex items-center gap-1"
        >
          {loading === m.key ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
          {m.label}
        </button>
      ))}
      {prev !== null && (
        <button
          type="button"
          onClick={undo}
          data-testid="copilot-bar-undo"
          className="text-[11px] px-2 py-0.5 rounded-full border border-slate-300 bg-slate-50 hover:bg-slate-100 text-slate-600 flex items-center gap-1"
        >
          <Undo2 className="w-3 h-3" /> Undo
        </button>
      )}
      {warn && <span className="text-[11px] text-amber-700" data-testid="copilot-bar-notice">{warn}</span>}
    </div>
  );
}
